import React, { useState, useRef, useEffect } from 'react';
import { Tab } from '@headlessui/react';
import { SparklesIcon, ArrowUpTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '../components/Button';
import ModelSelector from '../components/ModelSelector';
import GenerationTypeSelector from '../components/GenerationTypeSelector';
import { post, get } from '../api';
import '@google/model-viewer';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function AiStudioCreatePage() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [, setError] = useState(null);
  const [job, setJob] = useState(null);
  const [selectedModel, setSelectedModel] = useState('hunyuan-3.0');
  const [generationType, setGenerationType] = useState('Normal');

  // Initialize prompt from URL if present
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const promptParam = searchParams.get('prompt');
    if (promptParam) {
      setPrompt(promptParam);
    }
  }, []);

  // Image to 3D state
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Poll for job status
  useEffect(() => {
    if (job?.status === 'SUCCESS') {
      // Dispatch event to update credits
      window.dispatchEvent(new Event('credits-updated'));
      navigate({ to: `/app/ai-studio/model/${job.id}` });
      return;
    }

    if (!job || job.status === 'FAILED') return;

    const pollInterval = setInterval(async () => {
      try {
        const updatedJob = await get(`/api/1/generate/job/${job.id}`);
        setJob(updatedJob);
      } catch (e) {
        console.error("Polling failed", e);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [job, navigate]);

  const handleGenerateTextTo3D = async () => {
    setIsGenerating(true);
    setError(null);
    try {
      const response = await post('/api/1/generate/text-to-3d', {
        prompt,
        model: selectedModel,
        settings: {
          generate_type: generationType
        }
      });
      console.log('Generation started:', response);
      if (response.job_id) {
        // Start polling locally instead of redirecting
        setJob({ id: response.job_id, status: 'PENDING' });
      }
    } catch (err) {
      console.error('Failed to generate asset:', err);
      if (err.response && err.response.status === 402) {
        setError('Insufficient credits. Please upgrade your plan or purchase more credits.');
      } else {
        setError('Failed to start generation. Please try again.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('Image size must be less than 10MB');
        return;
      }

      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleImageSelect({ target: { files: [file] } });
    }
  };

  const handleGenerateImageTo3D = async () => {
    if (!selectedImage || !imagePreview) return;

    setIsGenerating(true);
    setError(null);
    try {
      const response = await post('/api/1/generate/image-to-3d', {
        image_base64: imagePreview,
        model: selectedModel,
        settings: {
          generate_type: generationType
        }
      });

      console.log('Generation started:', response);
      if (response.job_id) {
        setJob({ id: response.job_id, status: 'PENDING', generated_image_urls: [imagePreview] }); // Optimistically set image
      }
    } catch (err) {
      console.error('Failed to generate asset:', err);
      if (err.response && err.response.status === 402) {
        setError('Insufficient credits. Please upgrade your plan or purchase more credits.');
      } else {
        setError('Failed to start generation. Please try again.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const clearImage = (e) => {
    e.stopPropagation();
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const resetForm = () => {
    setJob(null);
    setPrompt('');
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (job) {
    const isSuccess = job.status === 'SUCCESS';
    const isFailed = job.status === 'FAILED';
    const isGenerating3D = job.status === 'GENERATING_3D';
    const hasImage = (job.generated_image_urls && job.generated_image_urls.length > 0) || job.generated_image_url;

    return (
      <div className="max-w-5xl mx-auto px-6 py-12 font-sans h-full flex flex-col">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground uppercase">
              {isSuccess ? 'Generation Complete' : 'Processing Asset'}
            </h1>
            <p className="text-xs font-mono text-muted-foreground mt-1 uppercase tracking-wider">
              JOB ID: {job.id}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isSuccess ? 'bg-green-500' : isFailed ? 'bg-red-500' : 'bg-yellow-500 animate-pulse'}`}></div>
            <span className="text-xs font-mono uppercase text-muted-foreground">
              {job.status}
            </span>
          </div>
        </div>

        <div className="flex-grow bg-black/5 dark:bg-white/5 rounded-2xl border border-border/50 overflow-hidden relative backdrop-blur-sm">
          <div className="absolute inset-0 flex items-center justify-center">
            {isSuccess ? (
              <div className="w-full h-full relative bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-gray-800 via-gray-950 to-black">
                <model-viewer
                  src={job.generated_model_url}
                  poster={job.generated_image_urls?.[0] || job.generated_image_url}
                  alt="Generated 3D model"
                  auto-rotate
                  camera-controls
                  shadow-intensity="1"
                  shadow-softness="1"
                  environment-image="neutral"
                  exposure="1.0"
                  style={{ width: '100%', height: '100%' }}
                >
                  <div slot="poster" className="flex items-center justify-center w-full h-full bg-background-soft">
                    <div className="w-16 h-16 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin"></div>
                  </div>
                </model-viewer>
                <div className="absolute bottom-6 right-6 flex gap-3">
                  <Button variant="secondary" onClick={() => window.open(job.generated_model_url, '_blank')} className="backdrop-blur-md bg-white/80 dark:bg-black/80 border border-white/20">
                    DOWNLOAD GLB
                  </Button>
                  <Button variant="primary" onClick={resetForm} className="shadow-lg shadow-purple-500/20">
                    CREATE NEW
                  </Button>
                </div>
              </div>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center p-12">
                <div className="relative w-full max-w-lg aspect-square rounded-xl overflow-hidden border border-white/10 shadow-2xl bg-black/20">
                  {hasImage ? (
                    <>
                      <img
                        src={job.generated_image_urls?.[0] || job.generated_image_url}
                        alt="Reference"
                        className={`w-full h-full object-cover transition-all duration-1000 ${isGenerating3D ? 'blur-sm scale-105 opacity-60' : ''}`}
                      />
                      {isGenerating3D && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-black/40 backdrop-blur-sm">
                          <div className="w-16 h-16 border-2 border-white/20 border-t-white rounded-full animate-spin mb-4"></div>
                          <span className="font-mono text-xs uppercase tracking-widest text-white">Constructing Geometry...</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                      <div className="w-16 h-16 border-2 border-purple-500/20 border-t-purple-500 rounded-full animate-spin mb-6"></div>
                      <h3 className="text-lg font-light text-foreground mb-2 uppercase tracking-widest">Dreaming...</h3>
                      <p className="text-xs font-mono text-muted-foreground max-w-xs">
                        Interpreting prompt vectors
                      </p>
                    </div>
                  )}
                </div>

                {isFailed && (
                  <div className="mt-8 p-4 bg-red-500/10 text-red-500 border border-red-500/20 rounded-lg max-w-md w-full text-center backdrop-blur-md">
                    <p className="font-bold uppercase text-xs tracking-wider mb-1">System Failure</p>
                    <p className="text-sm opacity-90">{job.error_message || 'An unexpected error occurred.'}</p>
                    <Button variant="secondary" size="sm" onClick={resetForm} className="mt-4 bg-red-500/10 hover:bg-red-500/20 border-red-500/30 text-red-500">
                      RETRY
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-12 font-sans min-h-full flex flex-col justify-center">
      <div className="mb-16 text-center relative">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-purple-500/20 blur-[100px] rounded-full pointer-events-none"></div>
        <h1 className="relative text-5xl md:text-7xl font-black tracking-tighter text-foreground mb-6 uppercase">
          Materialize <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">Ideas</span>
        </h1>
        <p className="relative text-muted-foreground tracking-[0.2em] uppercase text-xs font-medium">
          Advanced 3D Generation Engine v1.0
        </p>
      </div>

      <div className="backdrop-blur-2xl bg-background/40 border border-white/10 dark:border-white/5 rounded-3xl shadow-2xl relative max-w-4xl mx-auto w-full">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-foreground/10 m-6 rounded-tl-lg pointer-events-none"></div>
        <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-foreground/10 m-6 rounded-tr-lg pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-foreground/10 m-6 rounded-bl-lg pointer-events-none"></div>
        <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-foreground/10 m-6 rounded-br-lg pointer-events-none"></div>

        <Tab.Group>
          <div className="flex justify-center pt-8 pb-4">
            <Tab.List className="flex space-x-2 bg-black/5 dark:bg-white/5 p-1 rounded-full border border-white/10 backdrop-blur-md">
              <Tab
                className={({ selected }) =>
                  classNames(
                    'px-6 py-2 text-xs font-bold uppercase tracking-wider rounded-full transition-all duration-300 focus:outline-none',
                    selected
                      ? 'bg-foreground text-background shadow-lg'
                      : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                  )
                }
              >
                Text to 3D
              </Tab>
              <Tab
                className={({ selected }) =>
                  classNames(
                    'px-6 py-2 text-xs font-bold uppercase tracking-wider rounded-full transition-all duration-300 focus:outline-none',
                    selected
                      ? 'bg-foreground text-background shadow-lg'
                      : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                  )
                }
              >
                Image to 3D
              </Tab>
            </Tab.List>
          </div>

          <Tab.Panels className="p-8 md:p-12 min-h-[450px] flex flex-col justify-center">
            <Tab.Panel className="focus:outline-none space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 w-full">
              <div className="relative group">
                <textarea
                  rows={3}
                  className="w-full px-0 py-4 bg-transparent border-b-2 border-border/50 focus:border-purple-500 transition-all resize-none text-lg md:text-xl font-light text-foreground placeholder:text-muted-foreground/20 leading-tight focus:ring-0 focus:outline-none"
                  placeholder="A futuristic helmet..."
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                />
                <div className="absolute right-0 bottom-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-xs font-mono text-muted-foreground uppercase">Prompt Input</span>
                </div>
              </div>

              <div className="pt-2">
                <ModelSelector selectedModel={selectedModel} setSelectedModel={setSelectedModel} />
                {selectedModel === 'hunyuan-3.0' && (
                  <GenerationTypeSelector selectedType={generationType} setSelectedType={setGenerationType} />
                )}
              </div>

              <div className="flex items-center justify-between pt-4">
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-mono uppercase text-muted-foreground tracking-widest">
                    Estimated Build Time
                  </span>
                  <span className="text-sm font-medium">~2 Minutes</span>
                </div>

                <Button
                  variant="primary"
                  size="lg"
                  className="px-8 py-6 text-sm font-bold uppercase tracking-widest shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 transition-all hover:scale-105 bg-gradient-to-r from-indigo-600 to-purple-600 border-none"
                  onClick={handleGenerateTextTo3D}
                  disabled={!prompt || isGenerating}
                >
                  {isGenerating ? (
                    <span className="flex items-center gap-3">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Processing
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Generate Model <SparklesIcon className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </div>
            </Tab.Panel>

            <Tab.Panel className="focus:outline-none space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 w-full">
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*"
                onChange={handleImageSelect}
              />

              <div
                className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-300 cursor-pointer group overflow-hidden ${isDragging
                  ? 'border-purple-500 bg-purple-500/10 scale-[1.02] shadow-xl shadow-purple-500/20'
                  : imagePreview
                    ? 'border-purple-500/50 bg-purple-500/5'
                    : 'border-border/40 hover:border-purple-500/50 hover:bg-purple-500/5'
                  }`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {imagePreview ? (
                  <div className="relative z-10">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="max-h-64 mx-auto rounded-lg shadow-2xl transform group-hover:scale-105 transition-transform duration-500"
                    />
                    <button
                      onClick={clearImage}
                      className="absolute -top-4 -right-4 p-2 bg-background border border-border rounded-full shadow-lg hover:bg-red-500 hover:text-white hover:border-red-500 transition-all"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="relative z-10 py-8">
                    <div className="w-20 h-20 bg-background/50 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 border border-border/50 shadow-xl">
                      <ArrowUpTrayIcon className="w-8 h-8 text-muted-foreground group-hover:text-purple-500 transition-colors" />
                    </div>
                    <h3 className="text-lg font-medium text-foreground mb-2 uppercase tracking-wide">Upload Reference</h3>
                    <p className="text-muted-foreground text-xs font-mono max-w-xs mx-auto">
                      DROP IMAGE FILE OR CLICK TO BROWSE
                    </p>
                  </div>
                )}

                {/* Grid background effect */}
                <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
                  style={{ backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)', backgroundSize: '20px 20px' }}>
                </div>
              </div>

              <div className="pt-4">
                <ModelSelector selectedModel={selectedModel} setSelectedModel={setSelectedModel} />
                {selectedModel === 'hunyuan-3.0' && (
                  <GenerationTypeSelector selectedType={generationType} setSelectedType={setGenerationType} />
                )}
              </div>

              <div className="flex items-center justify-end pt-4">
                <Button
                  variant="primary"
                  size="lg"
                  className="px-8 py-6 text-sm font-bold uppercase tracking-widest shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 transition-all hover:scale-105 bg-gradient-to-r from-indigo-600 to-purple-600 border-none"
                  disabled={!selectedImage || isGenerating}
                  onClick={handleGenerateImageTo3D}
                >
                  {isGenerating ? (
                    <span className="flex items-center gap-3">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Processing
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Generate Model <SparklesIcon className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </div>
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>
  );
}
